import { History } from 'lucide-react';
import { useNavigate } from 'react-router';
import { type PatientVisit, PIN_PREFIX } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Card } from '#client/components/ui/Card';
import { type Column, DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Identifier } from '#client/components/ui/Identifier';
import { PaymentStatusBadge } from '#client/components/ui/StatusBadge';
import { useSubResource } from '#client/hooks/useResourceItem';
import { formatDateTime, formatMoney } from '#client/lib/format';
import { RESOURCE, routes } from '#client/lib/routes';

const columns: Column<PatientVisit>[] = [
  {
    key: 'date',
    header: 'Visit',
    render: (row) => formatDateTime(row.created_at),
    width: '12rem',
  },
  {
    key: 'pin',
    header: 'PIN',
    render: (row) => <Identifier value={row.pin_no} stripPrefix={PIN_PREFIX} />,
    width: '12rem',
  },
  {
    key: 'tests',
    header: 'Tests',
    render: (row) => {
      const done = row.reports.filter((report) => report.report_status === 'completed').length;
      return (
        <>
          {row.reports.map((report) => report.name).join(', ')}{' '}
          <span className="c-muted">
            ({done} of {row.reports.length} complete)
          </span>
        </>
      );
    },
  },
  {
    key: 'total',
    header: 'Total',
    render: (row) => formatMoney(row.total),
    align: 'end',
    width: '110px',
  },
  {
    key: 'payment',
    header: 'Payment',
    render: (row) => <PaymentStatusBadge status={row.payment_status} />,
    width: '120px',
  },
];

/** Everything else registered under this visit's MR number — the same phone. */
export function PreviousVisitsCard({ patientId, mrNo }: { patientId: number; mrNo: string }) {
  const navigate = useNavigate();
  const visits = useSubResource<PatientVisit[]>(RESOURCE.patients, patientId, 'visits');
  const rows = visits.data ?? [];

  return (
    <Card title={`Previous visits (${rows.length})`} subtitle={`MR number ${mrNo}`} flush>
      {visits.error && <Banner tone="critical">{visits.error}</Banner>}
      <DataTable
        caption={`Other visits under ${mrNo}`}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={visits.loading}
        stickyHeader={false}
        emptyState={
          <EmptyState
            icon={<History />}
            title="First visit"
            body="Visits registered with the same phone number will appear here."
          />
        }
        onRowClick={(row) => void navigate(routes.patient(row.id))}
      />
    </Card>
  );
}
